import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import {
  Banknote,
  Calculator,
  CreditCard,
  Globe,
  Receipt,
  RotateCcw,
  Scale,
  ShoppingBag,
  Tags,
  TrendingDown,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import apiClient from '@/api/client'
import { useCurrency } from '@/contexts/CurrencyContext'
import type { UseReportRange } from '@/hooks/useReportRange'
import type { OverviewReport, DailySalesRow } from '@/types'
import { metricPairOrEmpty } from '@/components/admin/dashboard/dashboardFormat'
import { MetricTile } from './MetricTile'
import { ExportButton } from './ExportButton'
import { ReportsExportSlot } from './ReportsExportSlot'
import { openPrintableReport, escapeHtml } from '@/lib/printReport'
import { useBusinessNameWithFallback } from '@/hooks/useBusinessName'
import { formatDateHuman, formatDateRangeHuman, parseIsoDate } from '@/lib/utils'
import {
  normalizeEnabledOrderTypes,
  displayLabelForOrderType,
  colorForOrderTypeId,
  type OrderTypeConfig,
} from '@/lib/orderTypeSettings'

const TENDER_COLORS: Record<string, string> = {
  cash: '#22c55e',
  card: '#3b82f6',
  online: '#a855f7',
  other: '#9ca3af',
}

// Fallback swatches for the order-type donut/breakdown when the tenant hasn't
// set a custom colour on a type. Mirrors OrderTypeMixBar's TYPE_COLORS (as hex,
// since Recharts Cell needs a colour value, not a Tailwind class).
const ORDER_TYPE_COLORS: Record<string, string> = {
  dine_in: '#22c55e',
  takeout: '#f97316',
  takeaway: '#f97316',
  delivery: '#3b82f6',
  counter: '#10b981',
  other: '#9ca3af',
}

function orderTypeColorFor(id: string, rows: OrderTypeConfig[]): string {
  return colorForOrderTypeId(rows, id) ?? ORDER_TYPE_COLORS[id] ?? ORDER_TYPE_COLORS.other
}

function tenderColor(method: string): string {
  return TENDER_COLORS[method] ?? TENDER_COLORS.other
}

function tenderIcon(method: string) {
  switch (method) {
    case 'cash':
      return <Banknote className="w-3.5 h-3.5" />
    case 'card':
      return <CreditCard className="w-3.5 h-3.5" />
    case 'online':
      return <Globe className="w-3.5 h-3.5" />
    default:
      return <Receipt className="w-3.5 h-3.5" />
  }
}

function tenderLabel(method: string): string {
  switch (method) {
    case 'cash':
      return 'Cash'
    case 'card':
      return 'Card'
    case 'online':
      return 'Online'
    default:
      return method.charAt(0).toUpperCase() + method.slice(1)
  }
}

interface Props {
  range: UseReportRange
}

export function OverviewTab({ range }: Props) {
  const { formatCurrency } = useCurrency()

  const overviewQuery = useQuery({
    queryKey: ['reports-v2', 'overview', range.fromISO, range.toISO],
    queryFn: () => apiClient.getReportsOverview(range.fromISO, range.toISO),
    staleTime: 30_000,
  })

  const dailyQuery = useQuery({
    queryKey: ['reports-v2', 'daily', range.fromISO, range.toISO],
    queryFn: () => apiClient.getDailySalesReport(range.fromISO, range.toISO),
    staleTime: 30_000,
  })

  // Order-type config so custom channels (e.g. "Foodpanda") resolve to their
  // real labels/colours in the order-type donut + breakdown.
  const { data: rawOrderTypes } = useQuery({
    queryKey: ['settings', 'enabled_order_types'],
    queryFn: async () => {
      const res = await apiClient.getSetting('enabled_order_types')
      return res.success && Array.isArray(res.data) ? res.data : []
    },
    staleTime: 60_000,
  })
  const orderTypeRows = useMemo(
    () => normalizeEnabledOrderTypes(rawOrderTypes ?? []),
    [rawOrderTypes],
  )

  const overview = overviewQuery.data?.data
  const coveragePct = overview
    ? metricPairOrEmpty(overview.projected_coverage_pct).current
    : 0
  const daily = dailyQuery.data?.data ?? []

  // Defensive: Go's `encoding/json` serializes nil slices as `null`, so any
  // empty-result array on `overview` may arrive as null from the API. Normalize
  // here once so the rest of the component can treat it as an array.
  const tenderMix = overview?.tender_mix ?? []
  // Online-provider breakdown (migration 050). Empty unless the tenant enabled
  // online sub-methods and attributed at least one online sale.
  const onlineSubMix = overview?.tender_mix_online_submethods ?? []
  // Sales segmented by order type, net of refunds (sums to gross-after-refunds).
  const orderTypeMix = overview?.order_type_mix ?? []

  const comparisonLabel = useMemo(() => {
    if (!overview) return 'vs. previous period'
    const prevFrom = parseIsoDate(overview.previous_from)
    const prevTo = parseIsoDate(overview.previous_to)
    if (!prevFrom || !prevTo) return 'vs. previous period'

    // Phrase the comparison relative to the active preset so the badge
    // reads as English rather than as two repeated dates. Falls back to
    // a generic "previous period" label when the preset is custom.
    const previousLabel = formatDateRangeHuman(prevFrom, prevTo)
    switch (range.preset) {
      case 'today':
        return `vs. yesterday (${previousLabel})`
      case 'yesterday':
        return `vs. day before (${previousLabel})`
      case 'last_7':
        return `vs. previous 7 days (${previousLabel})`
      case 'last_30':
        return `vs. previous 30 days (${previousLabel})`
      case 'this_month':
        return `vs. last month (${previousLabel})`
      case 'last_month':
        return `vs. month before (${previousLabel})`
      default:
        return `vs. previous period (${previousLabel})`
    }
  }, [overview, range.preset])

  const tenderTotal = tenderMix.reduce((s, r) => s + r.amount, 0)
  const orderTypeTotal = orderTypeMix.reduce((s, r) => s + r.amount, 0)
  const formatNumber = (n: number) => n.toLocaleString('en-US')

  const brand = useBusinessNameWithFallback()
  const handlePrintPdf = () => {
    if (!overview) return
    openPrintableReport({
      title: 'Sales Overview',
      subtitle: `${formatDateRangeHuman(range.from, range.to)} • ${overview.timezone}`,
      bodyHtml: buildOverviewPdf(overview, daily, formatCurrency),
      brand,
    })
  }

  return (
    <div className="space-y-6">
      <ReportsExportSlot>
        <ExportButton
          report="overview"
          reportLabel="Overview"
          fromISO={range.fromISO}
          toISO={range.toISO}
          onPrintPdf={handlePrintPdf}
          onPeriodPack={() => apiClient.exportReportXlsx('period_pack', range.fromISO, range.toISO)}
        />
      </ReportsExportSlot>

      <p className="text-xs text-muted-foreground">
        Showing data for{' '}
        <span className="font-medium text-foreground">
          {formatDateRangeHuman(range.from, range.to)}
        </span>
        . All numbers below cover this window.
      </p>

      {overviewQuery.isError && (
        <Card className="border-destructive/50">
          <CardContent className="p-4 text-sm text-destructive">
            Failed to load overview: {String((overviewQuery.error as Error)?.message ?? 'unknown')}
          </CardContent>
        </Card>
      )}

      {/* Wide screens: 4 + 4 + 3; last row ends with Projected margin & Net profit
          (dashboard-style border on those two via MetricTile `emphasised`). */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {overviewQuery.isLoading || !overview ? (
          Array.from({ length: 11 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-7 w-32" />
                <Skeleton className="h-4 w-40" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <MetricTile
              label="Gross sales after refunds"
              metricKey="gross_sales_after_refunds"
              metric={metricPairOrEmpty(overview.gross_sales_after_refunds)}
              formatValue={formatCurrency}
              comparisonLabel={comparisonLabel}
              icon={<Scale className="w-4 h-4" />}
            />
            <MetricTile
              label="Sales tax"
              metricKey="sales_tax"
              metric={overview.tax}
              formatValue={formatCurrency}
              comparisonLabel={comparisonLabel}
            />
            <MetricTile
              label="Discounts"
              metricKey="discounts"
              metric={overview.discounts}
              formatValue={formatCurrency}
              comparisonLabel={comparisonLabel}
              invertColors
              icon={<Tags className="w-4 h-4" />}
            />
            <MetricTile
              label="Refunds issued"
              metricKey="refunds_issued"
              metric={metricPairOrEmpty(overview.refunds_issued)}
              formatValue={formatCurrency}
              comparisonLabel={comparisonLabel}
              invertColors
              icon={<RotateCcw className="w-4 h-4" />}
            />
            <MetricTile
              label="Net sales"
              metricKey="net_sales"
              metric={overview.net_sales}
              formatValue={formatCurrency}
              comparisonLabel={comparisonLabel}
              icon={<Receipt className="w-4 h-4" />}
            />
            <MetricTile
              label="Orders"
              metricKey="orders"
              metric={overview.orders}
              formatValue={formatNumber}
              comparisonLabel={comparisonLabel}
              icon={<ShoppingBag className="w-4 h-4" />}
            />
            <MetricTile
              label="Avg order value"
              metricKey="avg_ticket"
              metric={overview.average_check}
              formatValue={formatCurrency}
              comparisonLabel={comparisonLabel}
            />
            <MetricTile
              label="Projected margin"
              metricKey="projected_margin"
              metric={metricPairOrEmpty(overview.projected_margin)}
              formatValue={formatCurrency}
              comparisonLabel={comparisonLabel}
              icon={<Calculator className="w-4 h-4" />}
              emphasised
              subtitle={
                <span>
                  {metricPairOrEmpty(overview.projected_margin_pct).current.toFixed(1)}% margin •{' '}
                  <span
                    className={coveragePct < 50 ? 'text-amber-600 dark:text-amber-400' : undefined}
                  >
                    {coveragePct.toFixed(0)}% covered
                  </span>
                </span>
              }
            />
            <MetricTile
              label="Net profit"
              metricKey="net_profit"
              metric={metricPairOrEmpty(overview.net_profit)}
              formatValue={formatCurrency}
              comparisonLabel={comparisonLabel}
              icon={<TrendingDown className="w-4 h-4" />}
              emphasised
              subtitle={
                <span>
                  After COGS &amp; expenses •{' '}
                  <span className={coveragePct < 50 ? 'text-amber-600 dark:text-amber-400' : undefined}>
                    {coveragePct.toFixed(0)}% covered
                  </span>
                </span>
              }
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Net sales trend</CardTitle>
          </CardHeader>
          <CardContent className="h-[280px]">
            {dailyQuery.isLoading ? (
              <Skeleton className="w-full h-full" />
            ) : daily.length === 0 ? (
              <EmptyState message="No sales in the selected range" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daily} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="rgba(148,163,184,0.18)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    tickFormatter={(iso: string) => formatDateHuman(iso)}
                  />
                  <YAxis
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => formatCompact(v)}
                    width={60}
                  />
                  <ReTooltip
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    formatter={(v: number) => [formatCurrency(v), 'Net sales']}
                  />
                  <Line type="monotone" dataKey="net" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tender mix</CardTitle>
          </CardHeader>
          <CardContent className="h-[280px]">
            {overviewQuery.isLoading ? (
              <Skeleton className="w-full h-full" />
            ) : !overview || tenderMix.length === 0 || tenderTotal === 0 ? (
              <EmptyState message="No payments yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tenderMix}
                    dataKey="amount"
                    nameKey="method"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {tenderMix.map((entry) => (
                      <Cell key={entry.method} fill={tenderColor(entry.method)} />
                    ))}
                  </Pie>
                  <ReTooltip
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    formatter={(v: number, name: string) => [formatCurrency(v), tenderLabel(String(name))]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconSize={8}
                    formatter={(value) => (
                      <span className="text-xs">{tenderLabel(String(value))}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {overview && tenderMix.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tender mix breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {tenderMix.map((row) => (
              <div key={row.method} className="space-y-1.5">
                <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/20 p-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span style={{ color: tenderColor(row.method) }}>{tenderIcon(row.method)}</span>
                    <span className="font-medium">{tenderLabel(row.method)}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {row.count} order{row.count === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm tabular-nums">
                    <span className="text-muted-foreground">{row.pct.toFixed(1)}%</span>
                    <span className="font-semibold">{formatCurrency(row.amount)}</span>
                  </div>
                </div>
                {/* Online provider sub-breakdown (migration 050) — indented under
                    the online row; amounts reconcile to the online total. */}
                {row.method === 'online' &&
                  onlineSubMix.map((sub) => (
                    <div
                      key={sub.slug || 'unattributed'}
                      className="ml-6 flex items-center justify-between gap-3 rounded-md border border-dashed border-border/70 bg-muted/10 px-3 py-2"
                    >
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">↳</span>
                        <span className="font-medium">{sub.label}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {sub.count} order{sub.count === 1 ? '' : 's'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs tabular-nums">
                        <span className="text-muted-foreground">{sub.pct.toFixed(1)}%</span>
                        <span className="font-semibold">{formatCurrency(sub.amount)}</span>
                      </div>
                    </div>
                  ))}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {overview && orderTypeMix.length > 0 && orderTypeTotal > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Order type mix breakdown</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {orderTypeMix.map((row) => (
                <div
                  key={row.order_type}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/20 p-3"
                >
                  <div className="flex items-center gap-2 text-sm">
                    <span
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                      style={{ backgroundColor: orderTypeColorFor(row.order_type, orderTypeRows) }}
                    />
                    <span className="font-medium">
                      {displayLabelForOrderType(row.order_type, orderTypeRows)}
                    </span>
                    <Badge variant="outline" className="text-[10px]">
                      {row.count} order{row.count === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm tabular-nums">
                    <span className="text-muted-foreground">{row.pct.toFixed(1)}%</span>
                    <span className="font-semibold">{formatCurrency(row.amount)}</span>
                  </div>
                </div>
              ))}
              <p className="pt-1 text-[11px] text-muted-foreground">
                Amounts are net of refunds and tie out to Gross sales after refunds.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Order type mix</CardTitle>
            </CardHeader>
            <CardContent className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={orderTypeMix}
                    dataKey="amount"
                    nameKey="order_type"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {orderTypeMix.map((entry) => (
                      <Cell
                        key={entry.order_type}
                        fill={orderTypeColorFor(entry.order_type, orderTypeRows)}
                      />
                    ))}
                  </Pie>
                  <ReTooltip
                    contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    formatter={(v: number, name: string) => [
                      formatCurrency(v),
                      displayLabelForOrderType(String(name), orderTypeRows),
                    ]}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconSize={8}
                    formatter={(value) => (
                      <span className="text-xs">
                        {displayLabelForOrderType(String(value), orderTypeRows)}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

function formatCompact(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return String(value)
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
      {message}
    </div>
  )
}

function buildOverviewPdf(
  overview: OverviewReport,
  daily: DailySalesRow[],
  formatCurrency: (n: number) => string,
): string {
  const kpis = [
    ['Net Sales', formatCurrency(overview.net_sales.current)],
    ['Discounts', formatCurrency(overview.discounts.current)],
    ['Tax', formatCurrency(overview.tax.current)],
    ['Orders', overview.orders.current.toLocaleString('en-US')],
    ['Avg Order Value', formatCurrency(overview.average_check.current)],
    ['Refunds Issued', formatCurrency(metricPairOrEmpty(overview.refunds_issued).current)],
    ['Gross Sales After Refunds', formatCurrency(metricPairOrEmpty(overview.gross_sales_after_refunds).current)],
    [
      'Projected Margin',
      `${formatCurrency(metricPairOrEmpty(overview.projected_margin).current)} (${metricPairOrEmpty(overview.projected_margin_pct).current.toFixed(1)}%, ${metricPairOrEmpty(overview.projected_coverage_pct).current.toFixed(0)}% covered)`,
    ],
    ['Net Profit', formatCurrency(metricPairOrEmpty(overview.net_profit).current)],
  ]
  const kvHtml = kpis
    .map(
      ([label, value]) =>
        `<div><span class="label">${escapeHtml(label)}</span><span class="value">${escapeHtml(value)}</span></div>`,
    )
    .join('')
  const tenderRows = (overview.tender_mix ?? [])
    .map(
      (t) =>
        `<tr><td>${escapeHtml(tenderLabel(t.method))}</td><td class="num">${t.count}</td><td class="num">${escapeHtml(formatCurrency(t.amount))}</td><td class="num">${t.pct.toFixed(1)}%</td></tr>`,
    )
    .join('')
  const dailyRows = daily
    .map(
      (d) =>
        `<tr><td>${escapeHtml(formatDateHuman(d.date))}</td><td class="num">${d.orders}</td><td class="num">${escapeHtml(formatCurrency(d.net))}</td></tr>`,
    )
    .join('')

  return `
    <section><div class="kv">${kvHtml}</div></section>
    <section><div class="section-title">Tender mix</div>
      <table><thead><tr><th>Method</th><th class="num">Orders</th><th class="num">Amount</th><th class="num">% of total</th></tr></thead>
        <tbody>${tenderRows || `<tr><td colspan="4" class="muted">No payments</td></tr>`}</tbody></table>
    </section>
    <section><div class="section-title">Daily sales</div>
      <table><thead><tr><th>Date</th><th class="num">Orders</th><th class="num">Guests</th><th class="num">Net</th></tr></thead>
        <tbody>${dailyRows || `<tr><td colspan="4" class="muted">No sales</td></tr>`}</tbody></table>
    </section>
  `
}
